import {
  STATUS_LABELS,
  TICKET_STATUSES,
  isRetiredTicketStatus,
  isTicketStatus,
  statusToSlug,
  type TicketStatus,
} from "@sd/constants/ticketStatuses";

import {
  emptyCreatedDateFilter,
  hasCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";

const TOTAL_KEYS = new Set([
  "total",
  "total_count",
  "count",
  "total_tickets",
  "grand_total",
]);

const SKIP_KEYS = new Set([
  ...TOTAL_KEYS,
  "success",
  "message",
  "code",
  "timezone",
  "tenant",
  "start_date",
  "end_date",
  "project",
  "filters",
  "meta",
]);

const NESTED_KEYS = [
  "data",
  "results",
  "status_counts",
  "statuses",
  "status_dashboard",
  "status_data",
  "counts",
  "payload",
];

const LABEL_TO_STATUS = new Map<string, TicketStatus>(
  Object.entries(STATUS_LABELS).map(([value, label]) => [
    label.trim().toLowerCase(),
    value as TicketStatus,
  ])
);

export interface StatusDashboardData {
  counts: Record<TicketStatus, number>;
  total?: number;
}

export interface StatusDashboardFilters {
  projectFilters: string[];
  createdDateFilter: CreatedDateFilterState;
}

export function parseStatusDashboardProjectParam(
  project: string | null | undefined
): string[] {
  const raw = String(project ?? "").trim();
  if (!raw || raw === "all") return [];
  return normalizeMultiFilterValues(raw.split(","));
}

export function formatStatusDashboardProjectParam(
  projectFilters: string[]
): string | undefined {
  const unique = normalizeMultiFilterValues(projectFilters);
  if (unique.length === 0) return undefined;
  return unique.join(",");
}

export function emptyStatusDashboardFilters(): StatusDashboardFilters {
  return {
    projectFilters: [],
    createdDateFilter: emptyCreatedDateFilter(),
  };
}

export function hasActiveStatusDashboardFilters(
  filters: StatusDashboardFilters
): boolean {
  return (
    normalizeMultiFilterValues(filters.projectFilters).length > 0 ||
    hasCreatedDateFilter(filters.createdDateFilter)
  );
}

/**
 * Query params for GET `/service_desk/status_dashboard/`.
 * Multiple projects: `project=uuid1,uuid2`
 */
export function buildStatusDashboardParams(
  filters: StatusDashboardFilters
): Record<string, string> {
  const params: Record<string, string> = {};
  const dates = normalizeCreatedDateFilter(filters.createdDateFilter);

  if (dates.from) params.start_date = dates.from;
  if (dates.to) params.end_date = dates.to;

  const project = formatStatusDashboardProjectParam(filters.projectFilters);
  if (project) params.project = project;

  return params;
}

function readCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value && typeof value === "object") {
    const row = value as Record<string, unknown>;
    return readCount(
      row.count ?? row.total ?? row.ticket_count ?? row.value ?? row.tickets
    );
  }
  return 0;
}

/** API may send `Total`, `total`, `total_count`, etc. at the root. */
function readExplicitTotalFromRecord(
  record: Record<string, unknown>
): number | undefined {
  for (const [key, value] of Object.entries(record)) {
    if (TOTAL_KEYS.has(statusToSlug(key))) {
      return readCount(value);
    }
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Prefer API-provided total — never sum status counts for display. */
function resolveDashboardTotal(
  ...records: (Record<string, unknown> | null | undefined)[]
): number | undefined {
  for (const record of records) {
    if (!record) continue;
    const explicit = readExplicitTotalFromRecord(record);
    if (explicit !== undefined) return explicit;
  }
  return undefined;
}

function recordHasDashboardFields(record: Record<string, unknown>): boolean {
  return (
    "status_counts" in record ||
    "statuses" in record ||
    "counts" in record ||
    readExplicitTotalFromRecord(record) !== undefined
  );
}

function emptyStatusCounts(): Record<TicketStatus, number> {
  return Object.fromEntries(
    TICKET_STATUSES.map(status => [status, 0])
  ) as Record<TicketStatus, number>;
}

/**
 * Map an API status key/label to a dashboard card.
 * Exact workflow statuses only — never alias retired keys (e.g. `fixed`)
 * or unknown keys into another status via normalizeStatus.
 */
function resolveStatusKey(raw: unknown): TicketStatus | null {
  if (raw === null || raw === undefined) return null;

  const labelMatch = LABEL_TO_STATUS.get(String(raw).trim().toLowerCase());
  if (labelMatch) return labelMatch;

  const slug = statusToSlug(raw);
  if (!slug || SKIP_KEYS.has(slug)) return null;
  if (isRetiredTicketStatus(slug)) return null;
  if (isTicketStatus(slug)) return slug;

  return null;
}

function mergeStatusCount(
  counts: Record<TicketStatus, number>,
  rawStatus: unknown,
  rawCount: unknown
) {
  const status = resolveStatusKey(rawStatus);
  if (!status) return;
  counts[status] = (counts[status] ?? 0) + readCount(rawCount);
}

function absorbStatusMap(
  counts: Record<TicketStatus, number>,
  map: Record<string, unknown>
) {
  for (const [key, value] of Object.entries(map)) {
    const slug = statusToSlug(key);
    if (SKIP_KEYS.has(slug)) continue;
    mergeStatusCount(counts, key, value);
  }
}

function absorbStatusRow(
  counts: Record<TicketStatus, number>,
  row: Record<string, unknown>
) {
  if ("status" in row || "status_name" in row || "name" in row) {
    mergeStatusCount(
      counts,
      row.status ?? row.status_name ?? row.name,
      row.count ?? row.total ?? row.ticket_count ?? row.value ?? row.tickets
    );
    return;
  }

  absorbStatusMap(counts, row);
}

function parseStatusDashboardFallback(payload: unknown): StatusDashboardData {
  const counts = emptyStatusCounts();
  let explicitTotal: number | undefined;

  const visit = (node: unknown, depth = 0) => {
    if (!node || depth > 8) return;

    if (Array.isArray(node)) {
      for (const item of node) {
        if (item && typeof item === "object") {
          absorbStatusRow(counts, item as Record<string, unknown>);
        }
      }
      return;
    }

    if (typeof node !== "object") return;
    const record = node as Record<string, unknown>;

    for (const [key, value] of Object.entries(record)) {
      if (TOTAL_KEYS.has(statusToSlug(key))) {
        explicitTotal = readCount(value);
      }
    }

    let visitedNested = false;
    for (const key of NESTED_KEYS) {
      if (key in record) {
        visitedNested = true;
        visit(record[key], depth + 1);
      }
    }

    const looksLikeStatusRow =
      "status" in record || "status_name" in record || "name" in record;

    if (!visitedNested || looksLikeStatusRow) {
      absorbStatusRow(counts, record);
    }
  };

  visit(payload);

  return {
    counts,
    total: explicitTotal,
  };
}

/**
 * Parse API payload:
 * `{ success, total, status_counts: { new: 1, ... } }`
 * Also accepts nested `data`, array `status_counts`, and label keys.
 */
export function parseStatusDashboardResponse(payload: unknown): StatusDashboardData {
  const outer = asRecord(payload);
  const root = unwrapStatusDashboardPayload(payload) ?? outer;
  if (!root) {
    return { counts: emptyStatusCounts(), total: undefined };
  }

  const statusCounts = root.status_counts ?? root.statuses ?? root.counts;

  if (Array.isArray(statusCounts)) {
    const counts = emptyStatusCounts();
    for (const item of statusCounts) {
      if (item && typeof item === "object") {
        absorbStatusRow(counts, item as Record<string, unknown>);
      }
    }
    const total = resolveDashboardTotal(root, outer);
    return { counts, total };
  }

  if (statusCounts && typeof statusCounts === "object") {
    const counts = emptyStatusCounts();
    absorbStatusMap(counts, statusCounts as Record<string, unknown>);
    const total = resolveDashboardTotal(root, outer);
    return { counts, total };
  }

  const fallback = parseStatusDashboardFallback(payload);
  return {
    ...fallback,
    total: resolveDashboardTotal(outer, root) ?? fallback.total,
  };
}

function unwrapStatusDashboardPayload(
  payload: unknown
): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  const nested = record.data ?? record.result ?? record.payload;

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    if (recordHasDashboardFields(inner)) {
      return inner;
    }
  }

  return record;
}
